import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  is_restaurant_owner: boolean;
  is_active: boolean;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  date_of_birth: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, isRestaurantOwner?: boolean, extraFields?: { address?: string; city?: string; postal_code?: string; date_of_birth?: string; phone?: string; country?: string }) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching profile:", error);
      return null;
    }
    return data as Profile;
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    }
  };

  /** Check profile is_active. If false, sign out and clear state. Returns true if user is allowed. */
  const checkAndSetUser = async (s: Session): Promise<boolean> => {
    const profileData = await fetchProfile(s.user.id);

    if (profileData && profileData.is_active === false) {
      console.log("[Auth] User deactivated, forcing sign-out:", s.user.email);
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setProfile(null);
      return false;
    }

    setSession(s);
    setUser(s.user);
    setProfile(profileData);

    // Handle pending referral code from signup
    const refCode = localStorage.getItem("TableGuide_ref_code");
    if (refCode) {
      localStorage.removeItem("TableGuide_ref_code");
      try {
        const { data: referrer } = await supabase
          .from("profiles")
          .select("user_id")
          .eq("referral_code", refCode as any)
          .single();

        if (referrer && referrer.user_id !== s.user.id) {
          await (supabase.from("referrals") as any).insert({
            referrer_id: referrer.user_id,
            referred_id: s.user.id,
            referral_code: refCode,
          });
        }
      } catch {
        // Referral already exists or code invalid – ignore
      }
    }

    return true;
  };

  // Polling: check is_active every 60 seconds for logged-in users.
  // Requires TWO consecutive `false` reads to avoid logging users out on a
  // transient RLS / network blip.
  useEffect(() => {
    if (!user) return;
    let consecutiveInactive = 0;

    const interval = setInterval(async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_active")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error || !data) {
        consecutiveInactive = 0;
        return;
      }

      if (data.is_active === false) {
        consecutiveInactive += 1;
        if (consecutiveInactive >= 2) {
          console.log("[Auth] User deactivated during session, forcing sign-out:", user.email);
          await supabase.auth.signOut();
          setUser(null);
          setSession(null);
          setProfile(null);
        }
      } else {
        consecutiveInactive = 0;
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    let mounted = true;

    const applySessionState = async (nextSession: Session | null) => {
      if (!mounted) return;

      if (nextSession?.user) {
        const allowed = await checkAndSetUser(nextSession);
        if (!mounted) return;
        if (!allowed) {
          setLoading(false);
          return;
        }
      } else {
        setSession(null);
        setUser(null);
        setProfile(null);
      }

      if (mounted) {
        setLoading(false);
      }
    };

    // CRITICAL: Subscribe FIRST so we never miss a SIGNED_IN event that fires
    // between getSession() and subscription setup (causes "double login" bug).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[Auth] onAuthStateChange:", event, "session:", !!session, "user:", session?.user?.email);
      if (!mounted) return;

      if (event === "TOKEN_REFRESHED" && !session) {
        console.warn("[Auth] Token refresh failed, clearing session");
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      // Defer async work to avoid deadlock inside the auth callback
      void applySessionState(session);
    });

    // Then restore the persisted session.
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("[Auth] getSession:", "session:", !!session, "user:", session?.user?.email);
      if (!mounted) return;
      void applySessionState(session);
    }).catch((error) => {
      console.error("[Auth] Failed to initialize session:", error);
      if (mounted) {
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    isRestaurantOwner = false,
    extraFields?: { address?: string; city?: string; postal_code?: string; date_of_birth?: string; phone?: string; country?: string }
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/home`,
        data: {
          full_name: fullName,
        },
      },
    });

    // Update profile with extra fields after signup
    if (!error && data.user && extraFields) {
      const updates: Record<string, string | undefined> = {};
      if (extraFields.address) updates.address = extraFields.address;
      if (extraFields.city) updates.city = extraFields.city;
      if (extraFields.postal_code) updates.postal_code = extraFields.postal_code;
      if (extraFields.date_of_birth) updates.date_of_birth = extraFields.date_of_birth;
      if (extraFields.phone) updates.phone = extraFields.phone;
      if (extraFields.country) updates.country = extraFields.country;

      if (Object.keys(updates).length > 0) {
        await supabase
          .from("profiles")
          .update(updates)
          .eq("user_id", data.user.id);
      }
    }

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data: statusData } = await supabase.functions.invoke("check-account-status", {
        body: { email },
      });

      if (statusData?.blocked) {
        setUser(null);
        setSession(null);
        setProfile(null);
        return { error: new Error("Dieses Konto wurde deaktiviert. Bitte kontaktieren Sie den Support.") };
      }
    } catch {
      // Fallback to auth login if status check is temporarily unavailable
    }

    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { error: error as Error | null };

    // Profile / is_active check is handled by onAuthStateChange → checkAndSetUser
    return { error: null };
  };

  const signOut = async () => {
    sessionStorage.removeItem("admin_2fa_verified_user_id");
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
